import axios from "axios";
import { backend_url } from "../../constants";
import { AXIOS_CONFIG } from "../api";
import { Endpoints } from "./endpoints";
import type { Tag, CreateTagRequest, UpdateTagRequest } from "@/types/tags";

const TAGS_URL = `${backend_url}/${Endpoints.tags}`;
const CLIENT_TAGS_URL = `${backend_url}/${Endpoints.clientTags}`;

/**
 * Fetches all tags for the current business.
 * @returns Promise<Tag[]>
 */
export const getTags = async (): Promise<Tag[]> => {
  try {
    const response = await axios.get<{ results: Tag[] }>(
      TAGS_URL,
      AXIOS_CONFIG
    );
    return response.data.results || [];
  } catch (error) {
    console.error("Error fetching tags:", error);
    throw error;
  }
};

/**
 * Creates a new tag.
 * @param data Tag creation data
 * @returns Promise<Tag>
 */
export const createTag = async (data: CreateTagRequest): Promise<Tag> => {
  try {
    const response = await axios.post<Tag>(TAGS_URL, data, AXIOS_CONFIG);
    return response.data;
  } catch (error) {
    console.error("Error creating tag:", error);
    throw error;
  }
};

/**
 * Updates an existing tag.
 * @param id Tag ID
 * @param data Tag update data
 * @returns Promise<Tag>
 */
export const updateTag = async (
  id: string,
  data: UpdateTagRequest
): Promise<Tag> => {
  try {
    const response = await axios.put<Tag>(
      `${TAGS_URL}${id}/`,
      data,
      AXIOS_CONFIG
    );
    return response.data;
  } catch (error) {
    console.error("Error updating tag:", error);
    throw error;
  }
};

/**
 * Deletes a tag.
 * @param id Tag ID
 * @returns Promise<void>
 */
export const deleteTag = async (id: string): Promise<void> => {
  try {
    await axios.delete(`${TAGS_URL}${id}/`, AXIOS_CONFIG);
  } catch (error) {
    console.error("Error deleting tag:", error);
    throw error;
  }
};

/**
 * Adds tags to a client.
 * @param clientId Client ID
 * @param tagIds Array of tag IDs to add
 * @returns Promise<void>
 */
export const addClientTags = async (
  clientId: string,
  tagIds: string[]
): Promise<void> => {
  try {
    await axios.post<void>(
      `${CLIENT_TAGS_URL}${clientId}/`,
      { tagIds },
      AXIOS_CONFIG
    );
  } catch (error) {
    console.error("Error adding client tags:", error);
    throw error;
  }
};

/**
 * Removes tags from a client.
 * @param clientId Client ID
 * @param tagIds Array of tag IDs to remove
 * @returns Promise<void>
 */
export const removeClientTags = async (
  clientId: string,
  tagIds: string[]
): Promise<void> => {
  try {
    await axios.delete(`${CLIENT_TAGS_URL}${clientId}/`, {
      data: { tagIds },
      ...AXIOS_CONFIG,
    });
  } catch (error) {
    console.error("Error removing client tags:", error);
    throw error;
  }
};
